import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Brain, Handshake, ArrowRight, CheckCircle } from 'lucide-react';
import { images } from '../data/mockData';

const Services = () => {
  const services = [
    {
      icon: Shield,
      title: 'Cybersecurity Readiness',
      description: 'Understanding what could go wrong and what to do about it',
      image: images.services.advisory,
      benefits: [
        'Plain explanation of what risks actually mean for your situation',
        'Practical steps you can take without hiring an IT department',
        'Help deciding what matters most and what can wait',
        'Training your staff will understand and remember',
        'NIST/TAC-aligned readiness reviews'
      ],
      outcome: 'You\'ll know what your real risks are, what\'s reasonable to do about them, and you won\'t waste money on things that don\'t help.'
    },
    {
      icon: Brain,
      title: 'AI Guidance',
      description: 'Real ways to use AI without the hype or risk',
      image: images.services.training,
      benefits: [
        'Straight talk about what AI can and can\'t do for you',
        'Specific uses that make sense for your work',
        'Help avoiding expensive experiments that go nowhere',
        'Using AI tools safely without risking your data'
      ],
      outcome: 'You\'ll know which tools are worth your time, how to use them properly, and what to avoid. No hype. No regrets.'
    },
    {
      icon: Handshake,
      title: 'Ongoing Advisory',
      description: 'Someone to call when questions come up',
      image: images.services.collaboration,
      benefits: [
        'A person to call when you\'re not sure what to do',
        'Second opinion on what vendors are proposing',
        'Help making decisions as things change',
        'Occasional check-ins to catch problems early'
      ],
      outcome: 'You\'ll have someone who knows your situation and can give you straight answers when you need them. No sales agenda.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Services
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            We help you understand what's happening, what matters, and what to do about it. No confusing reports. No pressure.
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#f5e6e0] rounded-lg mb-6">
                    <service.icon size={28} className="text-[#c84630]" />
                  </div>
                  <h2 className="text-3xl font-bold text-[#1e3a5f] mb-3">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">
                    What this includes:
                  </h3>
                  <ul className="space-y-3 mb-8">
                    {service.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle size={20} className="text-[#c84630] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-stone-50 p-6 rounded-lg border-l-4 border-[#c84630]">
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">What you'll get:</h4>
                    <p className="text-gray-700 leading-relaxed">{service.outcome}</p>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-lg shadow-lg w-full h-auto"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Support Section */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">
            Implementation Support (When Helpful)
          </h2>
          
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              After we help you understand what you actually need, some organizations ask if we can help carry it out. We can — but only if it makes sense.
            </p>
            
            <p>
              We don't lead with products. We don't push solutions. Our role stays advisory first.
            </p>
            
            <div className="py-4">
              <p className="mb-4">If requested, we can assist with:</p>
              <ul className="space-y-3 ml-1">
                <li className="flex items-start">
                  <span className="text-[#c84630] mr-3">•</span>
                  <span>Website setup and management</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#c84630] mr-3">•</span>
                  <span>Simple, practical AI tools tailored to daily operations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#c84630] mr-3">•</span>
                  <span>Network and firewall configuration guidance</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#c84630] mr-3">•</span>
                  <span>Vendor quote translation and contract review</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#c84630] mr-3">•</span>
                  <span>Project oversight to make sure work matches what was promised</span>
                </li>
              </ul>
            </div>
            
            <p>
              You're always free to implement recommendations yourself or use another provider. We're here to support decisions, not control them.
            </p>
            
            <p className="text-gray-600 italic pt-2">
              Our job is to help you make good decisions. If you'd like help carrying them out, we're glad to assist.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-12 text-center">
            How It Works
          </h2>
          <div className="space-y-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c84630] text-white rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                  Conversation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Tell us what you're trying to figure out. This is just a conversation. No forms, no commitment.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c84630] text-white rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                  Assessment
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We look at your situation and tell you what we see. In regular language, not technical reports.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c84630] text-white rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                  Clear Guidance
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We tell you what we'd do if we were in your shoes. You decide if that makes sense for you.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#c84630] text-white rounded-full flex items-center justify-center font-bold text-lg">
                4
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                  Ongoing Support (Optional)
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Stay in touch if you want. No obligation if you don't.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1e3a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Let's talk about what you need
          </h2>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">
            Tell us what's keeping you up at night. We'll tell you honestly if we can help.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#c84630] text-white font-medium rounded-md hover:bg-[#a83825] transition-colors text-lg"
          >
            Get in touch
            <ArrowRight size={20} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
